// photoWatchdog.js
//
// Silent client-side watchdog for the rare iOS/Safari incident where the
// uploaded photo layer blanks after the paper/margin re-render (Hotjar caught
// it but it leaves no server trace). It watches the photo <img>; if that image
// was rendering fine (naturalWidth > 0) and then goes blank while still in the
// DOM, it reports one diagnostic line to the MSCP audit log via
// /wp-json/mscp/v1/client_log. No UX impact, fully guarded, reports once per
// incident. Purpose: turn "we think it's a revoked blob URL" into hard data.
import Logger from './logger.js';

export default class PhotoWatchdog {
    static _started = false;

    static start() {
        if (this._started) return;
        this._started = true;
        this._goodImgs = new WeakSet();
        this._reported = new WeakSet();
        // Poll rather than rely on error events: the blanking happens on reflow,
        // which does not always fire an <img> error.
        setInterval(() => this._check(), 800);
    }

    static _photo() {
        return document.querySelector('img.mscp-layer.photo');
    }

    static _check() {
        try {
            const img = this._photo();
            if (!img) return;
            const good = img.complete && img.naturalWidth > 0;
            if (good) {
                this._goodImgs.add(img);
                return;
            }
            // Currently blank — but did we ever see it good, and is it still shown?
            const visible = img.isConnected && img.offsetParent !== null && (img.getAttribute('src') || '') !== '';
            if (visible && this._goodImgs.has(img) && !this._reported.has(img)) {
                this._reported.add(img);
                this._report(img);
            }
        } catch (e) {
            // never let the watchdog affect the app
        }
    }

    /**
     * Shared telemetry sender — POST one diagnostic line to the audit log.
     * Reused for step-gating telemetry (issue 3) as well as photo-blank.
     * @param {string} event  one of the server allowlist events
     * @param {object} context small flat object
     */
    static report(event, context = {}) {
        try {
            fetch('/wp-json/mscp/v1/client_log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ event, context }),
                keepalive: true,
            }).catch(() => {});
        } catch (e) {
            // telemetry must never throw
        }
    }

    static async _report(img) {
        try {
            const src = img.getAttribute('src') || '';
            const isBlob = src.startsWith('blob:');
            // Explicit strings so the log is unambiguous (a boolean false casts to
            // "" server-side, indistinguishable from "not a blob"). A revoked blob
            // URL fails to fetch — 'revoked' here is the smoking gun.
            let blobState = 'n/a';
            if (isBlob) {
                blobState = await fetch(src).then(() => 'reachable').catch(() => 'revoked');
            }
            const context = {
                src: src.slice(0, 30),
                is_blob: isBlob ? 'yes' : 'no',
                blob_state: blobState,
                natural_w: img.naturalWidth,
                complete: img.complete,
                step: (window.mscp && window.mscp.currentStep) || '',
                version: (window.mscp && window.mscp.version) || '',
                vw: window.innerWidth,
                vh: window.innerHeight,
                dpr: window.devicePixelRatio || 1,
            };
            Logger.debug('PhotoWatchdog: photo blanked, reporting', context);
            fetch('/wp-json/mscp/v1/client_log', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ event: 'photo_blank', context }),
                keepalive: true,
            }).catch(() => {});
        } catch (e) {
            // swallow — telemetry must never throw
        }
    }
}
